import {Project, ProjectAPI, Tag} from "@brg/api";
import { bProfile, useProfile } from "@brg/auth";
import { FlexBox, Skeleton, useBreakpoint } from "@brg/ui";
import {LayoutConst} from "core";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IProjectLight } from "shared/common";
import { ProjectsGridNew } from "shared/components/grids";
import { CreateLinkTo } from "shared/constants/linkTo";
import { computeProjectMapTile } from "shared/helpers/types/computed/ComputedProject";
import { AddBookChapterButton, AddBookChapterButtonContainer } from "./styles";
import { useTheme } from 'styled-components';
import BookPlusIcon from './assets/BookPlusIcon';
import BookVerticalCardIcon from './assets/BookVerticalCardIcon';
import {useDebouncedEffect} from "../../../../../shared/components/__FOX__/NAV/src/widgets/navbar-search-widget";
import { ItemType } from "shared/constants/itemType";

interface BookChaptersEditProps {
    bookId: string;
    chapters?: Project[];
    setChapters?: (val: Project[]) => void;
    setChapterIds?: (val: string[]) => void;
    isLoading?: boolean;
    canCreateChapter?: boolean;
    disabled?: boolean;
    getBookAuthorsIds: () => string[];
    setIsAllChaptersPublished: (value: boolean) => void;
    onCreateChapterClick: () => void;
    onSelectChaptersClick: () => void;
    onChapterAdd: (projectId: string) => void;
    update: () => void;
};

export const BookChaptersEdit: FC<BookChaptersEditProps> = ({
    bookId,
    chapters,
    isLoading,
    canCreateChapter,
    onCreateChapterClick,
    onSelectChaptersClick,
    setChapters,
    setChapterIds,
    update,
    disabled
}) => {
    const { t } = useTranslation();
    const profile = useProfile();
    const theme = useTheme();
    const is744 = useBreakpoint(LayoutConst.screen.s);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const history = useHistory();
    const [time, setTime] = useState<number | null>(null);

    useDebouncedEffect(
        () => {
            if (time) {
                update();
                setTime(null);
            }
        },
        [time, update],
        1000
    );

    const onChapterDelete = useCallback((projectId: string) => {

        const confirmation = window.confirm(t('confirmation.book_chapter_delete'));
        if (!confirmation) return;

        // предпоказ перед загрузкой данных
        const deletedChapterIndex = chapters?.findIndex(project => project?.projectId === projectId);
        if (deletedChapterIndex !== -1 && setChapters && setChapterIds) {
            const newChapters = [...(chapters || [])];
            newChapters?.splice(deletedChapterIndex || 0, 1);
            setChapters(newChapters)
            setChapterIds(newChapters.map(x => x.projectId));
        }

        ProjectAPI.RemoveBookInfo({
                bookChapterNumber: 0,
                bookId: bookId,
                projectId: projectId,
            })
            .then(() => {
                update();
                setTime(Date.now());
            });
    }, [bookId, update, chapters]);

    const onChapterSort = useCallback((projectId: string, newIndex: number): void => {

        // предпоказ перед загрузкой данных
        const draggedChapterIndex = chapters?.findIndex(project => project?.projectId === projectId);

        if (draggedChapterIndex !== -1 && setChapters && setChapterIds) {
            const newChapters = [...(chapters || [])];
            const temp = newChapters.splice(draggedChapterIndex || 0, 1)[0] || undefined

            if (temp) {
                newChapters.splice(newIndex, 0, temp);
                setChapters(newChapters)
                setChapterIds(newChapters.map(x => x.projectId));
            }
        }

        ProjectAPI.UpdateBookInfo({
                bookChapterNumber: newIndex + 1,
                bookId: bookId,
                projectId: projectId,
            })
    }, [bookId, update, chapters]);

    const onEdit = (projectId: string): void => {
        history.push(CreateLinkTo.bookProjectEdit(projectId))
    };

    const onView = (projectId: string): void => {
        history.push(CreateLinkTo.bookProject(projectId))
    };

    const onProjectClick = (project: IProjectLight, e?: any): void => {
        // disable navigation for bookmark btn
        if (e?.target?.tagName === "rect" || e?.target?.tagName === "svg" || e?.target?.tagName === "path" || (e?.target?.tagName === "DIV" && e?.target?.className.includes("BookmarkButtonWrapper"))) {
            e?.stopPropagation();
            e?.preventDefault()
            return
        }

        
        if (canEditProject(project)) {
            e?.stopPropagation();
            e?.preventDefault();
            onEdit(project.identifier);
            return;
        }

        if (canViewProject(project)) {
            e?.stopPropagation();
            e?.preventDefault();
            onView(project.identifier);
            return;
        }
    };

    const canEditProject = useCallback((project: IProjectLight): boolean => {

        if (disabled) return false;

        if (!profile || !project)
            return false;

        if (bProfile.isModer(profile))
            return true;

        if (bProfile.isMiddleModer(profile))
            return true;

        if (project.creatorId === profile.accountId)
            return true;

        if (project.authorId === profile.accountId)
            return true;

        if (project.coAuthorIds?.includes(profile.accountId))
            return true;

        if (project.curatorsIdList?.includes(profile.accountId))
            return true;
        
        return false;
    }, [profile, disabled]);

    const canViewProject = (project: IProjectLight): boolean => {
        if (!project)
            return false;

        if (project.status === 6 || project.status === 1 || project.status === 10)
            return true;
        
        return false;
    };

    if (isLoading) {
        return (
            <Skeleton width={'100%'} height={380}/>
        );
    }

    return (
        <>
            {(is744 && (chapters || [])?.length === 0) ? (
                <FlexBox directionMobile="column" widthMobile={'100%'}>
                    <AddBookChapterButtonContainer isEmptyChapters={(chapters || [])?.length === 0}>
                        <AddBookChapterButton isEmptyChapters={(chapters || [])?.length === 0} onClick={onCreateChapterClick} disabled={!canCreateChapter}>
                            <BookPlusIcon fill={theme.projectView.viewCardStroke}/>
                            {t('book_edit.chapters.button_create')}
                        </AddBookChapterButton>
                        <AddBookChapterButton isEmptyChapters={(chapters || [])?.length === 0} onClick={onSelectChaptersClick} disabled={disabled}>
                            <BookVerticalCardIcon fill={theme.projectView.viewCardStroke}/>
                            {t('book_edit.chapters.button_exists')}
                        </AddBookChapterButton>
                    </AddBookChapterButtonContainer>
                </FlexBox>
            ) : (
                <ProjectsGridNew
                    // @ts-ignore
                    projects={chapters?.map(computeProjectMapTile)}
                    isEditable={canEditProject}
                    isDeletable={() => false}
                    isRemovableFromBook={true}
                    isModal={true}
                    withoutRepost
                    isMobileMoreVisible={is744}
                    isDraggable={!disabled}
                    onEdit={onEdit}
                    onRemoveFromBook={onChapterDelete}
                    onProjectClick={onProjectClick}
                    onSortEnd={onChapterSort}
                    isCanNotBeBookmark
                    itemType={ItemType.book}
                >
                <AddBookChapterButtonContainer itemType={ItemType.book}>
                    <AddBookChapterButton onClick={onCreateChapterClick} disabled={!canCreateChapter}>
                        <BookPlusIcon fill={theme.projectView.viewCardStroke}/>
                        {t('book_edit.chapters.button_create')}
                    </AddBookChapterButton>
                    <AddBookChapterButton onClick={onSelectChaptersClick} disabled={disabled} withStroke>
                        <BookVerticalCardIcon fill={theme.projectView.viewCardStroke}/>
                        {t('book_edit.chapters.button_exists')}
                    </AddBookChapterButton>
                </AddBookChapterButtonContainer>
            </ProjectsGridNew>
            )}
        </>
    );
};import {Project, ProjectAPI, Tag} from "@brg/api";
import { bProfile, useProfile } from "@brg/auth";
import { FlexBox, Skeleton, useBreakpoint } from "@brg/ui";
import {LayoutConst} from "core";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IProjectLight } from "shared/common";
import { ProjectsGridNew } from "shared/components/grids";
import { CreateLinkTo } from "shared/constants/linkTo";
import { computeProjectMapTile } from "shared/helpers/types/computed/ComputedProject";
import { AddBookChapterButton, AddBookChapterButtonContainer } from "./styles";
import { useTheme } from 'styled-components';
import BookPlusIcon from './assets/BookPlusIcon';
import BookVerticalCardIcon from './assets/BookVerticalCardIcon';
import {useDebouncedEffect} from "../../../../../shared/components/__FOX__/NAV/src/widgets/navbar-search-widget";
import { ItemType } from "shared/constants/itemType";

interface BookChaptersEditProps {
    bookId: string;
    chapters?: Project[];
    setChapters?: (val: Project[]) => void;
    setChapterIds?: (val: string[]) => void;
    isLoading?: boolean;
    canCreateChapter?: boolean;
    disabled?: boolean;
    getBookAuthorsIds: () => string[];
    setIsAllChaptersPublished: (value: boolean) => void;
    onCreateChapterClick: () => void;
    onSelectChaptersClick: () => void;
    onChapterAdd: (projectId: string) => void;
    update: () => void;
};

export const BookChaptersEdit: FC<BookChaptersEditProps> = ({
    bookId,
    chapters,
    isLoading,
    canCreateChapter,
    onCreateChapterClick,
    onSelectChaptersClick,
    setChapters,
    setChapterIds,
    update,
    disabled
}) => {
    const { t } = useTranslation();
    const profile = useProfile();
    const theme = useTheme();
    const is744 = useBreakpoint(LayoutConst.screen.s);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const history = useHistory();
    const [time, setTime] = useState<number | null>(null);

    useDebouncedEffect(
        () => {
            if (time) {
                update();
                setTime(null);
            }
        },
        [time, update],
        1000
    );

    const onChapterDelete = useCallback((projectId: string) => {

        const confirmation = window.confirm(t('confirmation.book_chapter_delete'));
        if (!confirmation) return;

        // предпоказ перед загрузкой данных
        const deletedChapterIndex = chapters?.findIndex(project => project?.projectId === projectId);
        if (deletedChapterIndex !== -1 && setChapters && setChapterIds) {
            const newChapters = [...(chapters || [])];
            newChapters?.splice(deletedChapterIndex || 0, 1);
            setChapters(newChapters)
            setChapterIds(newChapters.map(x => x.projectId));
        }

        ProjectAPI.RemoveBookInfo({
                bookChapterNumber: 0,
                bookId: bookId,
                projectId: projectId,
            })
            .then(() => {
                update();
                setTime(Date.now());
            });
    }, [bookId, update, chapters]);

    const onChapterSort = useCallback((projectId: string, newIndex: number): void => {

        // предпоказ перед загрузкой данных
        const draggedChapterIndex = chapters?.findIndex(project => project?.projectId === projectId);

        if (draggedChapterIndex !== -1 && setChapters && setChapterIds) {
            const newChapters = [...(chapters || [])];
            const temp = newChapters.splice(draggedChapterIndex || 0, 1)[0] || undefined

            if (temp) {
                newChapters.splice(newIndex, 0, temp);
                setChapters(newChapters)
                setChapterIds(newChapters.map(x => x.projectId));
            }
        }

        ProjectAPI.UpdateBookInfo({
                bookChapterNumber: newIndex + 1,
                bookId: bookId,
                projectId: projectId,
            })
    }, [bookId, update, chapters]);

    const onEdit = (projectId: string): void => {
        history.push(CreateLinkTo.bookProjectEdit(projectId))
    };

    const onView = (projectId: string): void => {
        history.push(CreateLinkTo.bookProject(projectId))
    };

    const onProjectClick = (project: IProjectLight, e?: any): void => {
        // disable navigation for bookmark btn
        if (e?.target?.tagName === "rect" || e?.target?.tagName === "svg" || e?.target?.tagName === "path" || (e?.target?.tagName === "DIV" && e?.target?.className.includes("BookmarkButtonWrapper"))) {
            e?.stopPropagation();
            e?.preventDefault()
            return
        }

        
        if (canEditProject(project)) {
            e?.stopPropagation();
            e?.preventDefault();
            onEdit(project.identifier);
            return;
        }

        if (canViewProject(project)) {
            e?.stopPropagation();
            e?.preventDefault();
            onView(project.identifier);
            return;
        }
    };

    const canEditProject = useCallback((project: IProjectLight): boolean => {

        if (disabled) return false;

        if (!profile || !project)
            return false;

        if (bProfile.isModer(profile))
            return true;

        if (bProfile.isMiddleModer(profile))
            return true;

        if (project.creatorId === profile.accountId)
            return true;

        if (project.authorId === profile.accountId)
            return true;

        if (project.coAuthorIds?.includes(profile.accountId))
            return true;

        if (project.curatorsIdList?.includes(profile.accountId))
            return true;
        
        return false;
    }, [profile, disabled]);

    const canViewProject = (project: IProjectLight): boolean => {
        if (!project)
            return false;

        if (project.status === 6 || project.status === 1 || project.status === 10)
            return true;
        
        return false;
    };

    if (isLoading) {
        return (
            <Skeleton width={'100%'} height={380}/>
        );
    }

    return (
        <>
            {(is744 && (chapters || [])?.length === 0) ? (
                <FlexBox directionMobile="column" widthMobile={'100%'}>
                    <AddBookChapterButtonContainer isEmptyChapters={(chapters || [])?.length === 0}>
                        <AddBookChapterButton isEmptyChapters={(chapters || [])?.length === 0} onClick={onCreateChapterClick} disabled={!canCreateChapter}>
                            <BookPlusIcon fill={theme.projectView.viewCardStroke}/>
                            {t('book_edit.chapters.button_create')}
                        </AddBookChapterButton>
                        <AddBookChapterButton isEmptyChapters={(chapters || [])?.length === 0} onClick={onSelectChaptersClick} disabled={disabled}>
                            <BookVerticalCardIcon fill={theme.projectView.viewCardStroke}/>
                            {t('book_edit.chapters.button_exists')}
                        </AddBookChapterButton>
                    </AddBookChapterButtonContainer>
                </FlexBox>
            ) : (
                <ProjectsGridNew
                    // @ts-ignore
                    projects={chapters?.map(computeProjectMapTile)}
                    isEditable={canEditProject}
                    isDeletable={() => false}
                    isRemovableFromBook={true}
                    isModal={true}
                    withoutRepost
                    isMobileMoreVisible={is744}
                    isDraggable={!disabled}
                    onEdit={onEdit}
                    onRemoveFromBook={onChapterDelete}
                    onProjectClick={onProjectClick}
                    onSortEnd={onChapterSort}
                    isCanNotBeBookmark
                    itemType={ItemType.book}
                >
                <AddBookChapterButtonContainer itemType={ItemType.book}>
                    <AddBookChapterButton onClick={onCreateChapterClick} disabled={!canCreateChapter}>
                        <BookPlusIcon fill={theme.projectView.viewCardStroke}/>
                        {t('book_edit.chapters.button_create')}
                    </AddBookChapterButton>
                    <AddBookChapterButton onClick={onSelectChaptersClick} disabled={disabled} withStroke>
                        <BookVerticalCardIcon fill={theme.projectView.viewCardStroke}/>
                        {t('book_edit.chapters.button_exists')}
                    </AddBookChapterButton>
                </AddBookChapterButtonContainer>
            </ProjectsGridNew>
            )}
        </>
    );
};